import React, { useEffect, useMemo, useState } from 'react'
import { Button, Form, Modal, Spinner } from 'react-bootstrap'
import FeatherIcon from 'feather-icons-react'
import Select from 'react-select'

import {
  EXCEL_FONT_OPTIONS,
  getExcelProfileFont,
  isValidExcelFontSize,
  createAutoExcelColumns,
  createExcelProfileJson,
  getExcelDataSources,
  getExcelProfile,
} from './excelProfile.util'

const TYPE_OPTIONS = [
  { label: 'ข้อความ', value: 'text' },
  { label: 'ตัวเลข', value: 'number' },
  { label: 'วันที่', value: 'date' },
  { label: 'วันที่และเวลา', value: 'datetime' },
  { label: 'ใช่/ไม่ใช่', value: 'boolean' },
]

const PREVIEW_ROW_LIMIT = 200

const sanitizeFileName = (value = '') =>
  `${value || 'report'}`
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const sanitizeSheetName = (value = '') =>
  sanitizeFileName(value).replace(/[\[\]]+/g, ' ').slice(0, 31) || 'Data'

const getDisplayValue = (value, type) => {
  if (value === null || typeof value === 'undefined') return ''
  if (typeof value === 'object') return JSON.stringify(value)
  if (type === 'boolean') return value ? 'ใช่' : 'ไม่ใช่'
  if (type === 'number' && Number.isFinite(Number(value))) {
    return Number(value).toLocaleString('th-TH')
  }
  if (['date', 'datetime'].includes(type)) {
    const date = new Date(value)
    if (!Number.isNaN(date.getTime())) {
      return type === 'date'
        ? date.toLocaleDateString('th-TH')
        : date.toLocaleString('th-TH')
    }
  }

  return String(value)
}

const getExcelValue = (value, type) => {
  if (value === null || typeof value === 'undefined') return ''
  if (type === 'number') {
    const numberValue = Number(value)
    return Number.isFinite(numberValue) ? numberValue : value
  }
  if (type === 'boolean') return Boolean(value)
  if (['date', 'datetime'].includes(type)) {
    const dateValue = new Date(value)
    return Number.isNaN(dateValue.getTime()) ? value : dateValue
  }
  if (typeof value === 'object') return JSON.stringify(value)

  return value
}

const ExcelDataPreview = ({
  report = {},
  onDataSourceChange,
  onProfileChange,
  onProfileReset,
}) => {
  const dataSources = useMemo(() => getExcelDataSources(report), [report])
  const [selectedDataSourceId, setSelectedDataSourceId] = useState('')
  const [columnSettings, setColumnSettings] = useState({})
  const [exportOptions, setExportOptions] = useState({})
  const [showSettings, setShowSettings] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [copyStatus, setCopyStatus] = useState('')
  const [exportError, setExportError] = useState('')
  const dataSourceSignature = dataSources.map(({ id }) => id).join('|')
  const configuredDataSourceId = String(
    report?.publishData?.excelProfile?.dataSourceId || '',
  )

  useEffect(() => {
    setSelectedDataSourceId((current) => {
      if (dataSources.some(({ id }) => id === configuredDataSourceId)) {
        return configuredDataSourceId
      }
      return dataSources.some(({ id }) => id === current)
        ? current
        : dataSources[0]?.id || ''
    })
  }, [configuredDataSourceId, dataSourceSignature])

  useEffect(() => {
    setColumnSettings({})
    setExportOptions({})
    setCopyStatus('')
  }, [report?.reportTemplateId])

  const selectedDataSource = dataSources.find(
    ({ id }) => id === selectedDataSourceId,
  ) || dataSources[0]
  const savedProfile = getExcelProfile(report, selectedDataSource?.id)
  const defaultColumns = useMemo(
    () => createAutoExcelColumns(selectedDataSource),
    [selectedDataSource],
  )
  const autoColumns = useMemo(
    () => createAutoExcelColumns(selectedDataSource, savedProfile),
    [selectedDataSource, savedProfile],
  )
  const columns = columnSettings[selectedDataSource?.id] || autoColumns
  const visibleColumns = columns.filter(
    ({ exportable, visible }) => exportable !== false && visible !== false,
  )
  const rows = selectedDataSource?.rows || []
  const sourceOptions = dataSources.map(({ id, label }) => ({
    label,
    value: id,
  }))
  const selectedSourceOption = sourceOptions.find(
    ({ value }) => value === selectedDataSource?.id,
  ) || null
  const defaultExportOptions = {
    fileName: report?.reportName || selectedDataSource?.label,
    sheetName: selectedDataSource?.label || 'Data',
    fontFamily: '',
    fontSize: '',
  }
  const currentExportOptions = exportOptions[selectedDataSource?.id] || {
    fileName: savedProfile?.fileName || defaultExportOptions.fileName,
    sheetName: savedProfile?.sheetName || defaultExportOptions.sheetName,
    fontFamily: savedProfile?.fontFamily || '',
    fontSize: savedProfile?.fontSize ?? '',
  }

  const profileFont = getExcelProfileFont(currentExportOptions, report.layoutData)
  const defaultFont = getExcelProfileFont({}, report.layoutData)
  const invalidFontSize = currentExportOptions.fontSize !== '' &&
    !isValidExcelFontSize(currentExportOptions.fontSize)
  const fontOptions = useMemo(() => {
    const options = [
      { label: `ตามฟอนต์รายงาน (${defaultFont.fontFamily})`, value: '' },
      ...EXCEL_FONT_OPTIONS,
    ]
    const current = currentExportOptions.fontFamily
    if (current && !options.some(({ value }) => value === current)) {
      options.push({ label: current, value: current })
    }
    return options
  }, [defaultFont.fontFamily, currentExportOptions.fontFamily])

  const setCurrentColumns = (nextColumns) => {
    if (!selectedDataSource?.id) return
    setColumnSettings((current) => ({
      ...current,
      [selectedDataSource.id]: nextColumns,
    }))
  }

  const updateColumn = (field, patch) => {
    setCurrentColumns(
      columns.map((column) =>
        column.field === field ? { ...column, ...patch } : column,
      ),
    )
  }

  const moveColumn = (index, direction) => {
    const nextIndex = index + direction
    if (nextIndex < 0 || nextIndex >= columns.length) return

    const nextColumns = [...columns]
    const [column] = nextColumns.splice(index, 1)
    nextColumns.splice(nextIndex, 0, column)
    setCurrentColumns(nextColumns)
  }

  const updateExportOption = (key, value) => {
    if (!selectedDataSource?.id) return
    setExportOptions((current) => ({
      ...current,
      [selectedDataSource.id]: {
        ...currentExportOptions,
        [key]: value,
      },
    }))
  }

  const resetProfile = () => {
    if (!selectedDataSource?.id) return
    setColumnSettings((current) => {
      const next = { ...current }
      delete next[selectedDataSource.id]
      return next
    })
    setExportOptions((current) => {
      const next = { ...current }
      delete next[selectedDataSource.id]
      return next
    })
    setCopyStatus('')
    if (onProfileReset) onProfileReset(selectedDataSource.id)
  }

  const applyProfile = () => {
    if (!selectedDataSource?.id || invalidFontSize) return

    const profile = createExcelProfileJson(
      columns,
      currentExportOptions,
      defaultColumns,
      defaultExportOptions,
    )
    if (Object.keys(profile).length) {
      if (onProfileChange) onProfileChange(selectedDataSource.id, profile)
    } else if (onProfileReset) {
      onProfileReset(selectedDataSource.id)
    }
    setShowSettings(false)
  }

  const copyProfileJson = async () => {
    if (invalidFontSize) return
    const profile = createExcelProfileJson(
      columns,
      currentExportOptions,
      defaultColumns,
      defaultExportOptions,
    )

    try {
      await navigator.clipboard.writeText(JSON.stringify(profile, null, 2))
      setCopyStatus('คัดลอก JSON แล้ว')
    } catch (error) {
      console.error('Unable to copy Excel profile JSON', error)
      setCopyStatus('ไม่สามารถคัดลอก JSON ได้')
    }
  }

  const exportExcel = () => {
    if (!visibleColumns.length || !selectedDataSource || invalidFontSize) return

    setIsExporting(true)
    setExportError('')
    window.setTimeout(async () => {
      try {
        const XLSX = await import('xlsx-js-style')
        const worksheet = XLSX.utils.aoa_to_sheet(
          [
            visibleColumns.map(({ label }) => label),
            ...rows.map((row) =>
              visibleColumns.map(({ field, type }) =>
                getExcelValue(row?.[field], type),
              ),
            ),
          ],
          { cellDates: true },
        ) || {}
        const font = { name: profileFont.fontFamily, sz: profileFont.fontSize }
        const dataStyle = { font, alignment: { vertical: 'center', wrapText: true } }
        const headerStyle = { ...dataStyle, font: { ...font, bold: true } }
        for (let rowIndex = 0; rowIndex <= rows.length; rowIndex += 1) {
          visibleColumns.forEach((column, columnIndex) => {
            const address = XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex })
            if (worksheet[address]) {
              worksheet[address].s = rowIndex === 0 ? headerStyle : dataStyle
            }
          })
        }
        worksheet['!cols'] = visibleColumns.map(({ width }) => ({
          wch: Number(width) || 18,
        }))

        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(
          workbook,
          worksheet,
          sanitizeSheetName(currentExportOptions.sheetName),
        )
        XLSX.writeFile(
          workbook,
          `${sanitizeFileName(currentExportOptions.fileName)}.xlsx`,
        )
      } catch (error) {
        console.error('Unable to export styled Excel', error)
        setExportError('ดาวน์โหลด Excel ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง')
      } finally {
        setIsExporting(false)
      }
    }, 0)
  }

  if (!dataSources.length) {
    return (
      <div className="bg-white text-center text-muted p-5">
        <FeatherIcon icon="database" size={28} className="mb-2" />
        <div>ไม่พบ datasource ที่สามารถแสดงเป็นตารางได้</div>
      </div>
    )
  }

  return (
    <div className="bg-white p-3" style={{ minHeight: 460 }}>
      <div className="d-flex flex-wrap justify-content-between align-items-end mb-3">
        <div style={{ minWidth: 260 }}>
          <div className="font-weight-bold mb-1">Datasource</div>
          {sourceOptions.length > 1 ? (
            <Select
              isClearable={false}
              menuPlacement="auto"
              menuPortalTarget={
                typeof document !== 'undefined' ? document.body : null
              }
              menuPosition="fixed"
              onChange={(option) => {
                const dataSourceId = option?.value || ''
                setSelectedDataSourceId(dataSourceId)
                if (onDataSourceChange) onDataSourceChange(dataSourceId)
              }}
              options={sourceOptions}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
              value={selectedSourceOption}
            />
          ) : (
            <div className="form-control bg-light">{selectedDataSource.label}</div>
          )}
        </div>

        <div className="d-flex mt-2">
          <Button
            className="mr-2"
            aria-label="ตั้งค่าคอลัมน์" title="ตั้งค่าคอลัมน์ ฟอนต์ และขนาดตัวอักษร Excel"
            onClick={() => setShowSettings(true)}
            variant="outline-secondary"
          >
            <FeatherIcon icon="settings" size={16} className="mr-1" />
            ตั้งค่าคอลัมน์
          </Button>
          <Button
            aria-label="Export Excel" title="ดาวน์โหลด Excel ตามคอลัมน์และฟอนต์ที่เลือก"
            disabled={isExporting || !visibleColumns.length || invalidFontSize}
            onClick={exportExcel}
            variant="success"
          >
            {isExporting ? (
              <Spinner animation="border" size="sm" className="mr-1" />
            ) : (
              <FeatherIcon icon="download" size={16} className="mr-1" />
            )}
            Export Excel
          </Button>
        </div>
      </div>

      {exportError && <div className="alert alert-danger" role="alert">{exportError}</div>}
      <div className="text-muted mb-2" aria-label="ฟอนต์ Excel ปัจจุบัน">
        ฟอนต์ Excel: {profileFont.fontFamily} · {profileFont.fontSize} pt
      </div>
      <div className="d-flex justify-content-between text-muted mb-2">
        <span>{rows.length.toLocaleString('th-TH')} รายการ</span>
        <span>{visibleColumns.length} คอลัมน์</span>
      </div>

      <div className="table-responsive border" style={{ maxHeight: '65vh' }}>
        <table
          className="table table-sm table-bordered table-striped table-hover mb-0"
          style={{ fontFamily: profileFont.previewFontFamily, fontSize: `${profileFont.fontSize}pt`, lineHeight: 1.4 }}
        >
          <thead className="thead-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
            <tr>
              <th className="text-center" style={{ minWidth: 58 }}>#</th>
              {visibleColumns.map((column) => (
                <th
                  key={column.field}
                  style={{ minWidth: Math.max(Number(column.width) || 18, 10) * 8 }}
                >
                  <div>{column.label}</div>
                  <small className="font-weight-normal text-muted">
                    {column.field}
                  </small>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, PREVIEW_ROW_LIMIT).map((row, rowIndex) => (
              <tr key={rowIndex}>
                <td className="text-center text-muted">{rowIndex + 1}</td>
                {visibleColumns.map((column) => (
                  <td key={column.field}>
                    {getDisplayValue(row?.[column.field], column.type)}
                  </td>
                ))}
              </tr>
            ))}
            {!rows.length && (
              <tr>
                <td
                  className="text-center text-muted p-4"
                  colSpan={visibleColumns.length + 1}
                >
                  ไม่พบข้อมูลตามเงื่อนไขที่เลือก
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {rows.length > PREVIEW_ROW_LIMIT && (
        <div className="text-muted mt-2">
          แสดงตัวอย่าง {PREVIEW_ROW_LIMIT.toLocaleString('th-TH')} รายการแรก
          โดยไฟล์ Excel จะมีข้อมูลครบ {rows.length.toLocaleString('th-TH')} รายการ
        </div>
      )}

      <Modal
        centered
        onHide={() => setShowSettings(false)}
        show={showSettings}
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>ตั้งค่าตารางและ Excel Profile</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Row>
            <Form.Group className="col-md-6">
              <Form.Label>ชื่อไฟล์</Form.Label>
              <Form.Control
                onChange={(event) => updateExportOption('fileName', event.target.value)}
                value={currentExportOptions.fileName || ''}
              />
            </Form.Group>
            <Form.Group className="col-md-6">
              <Form.Label>ชื่อ Sheet</Form.Label>
              <Form.Control
                maxLength={31}
                onChange={(event) => updateExportOption('sheetName', event.target.value)}
                value={currentExportOptions.sheetName || ''}
              />
            </Form.Group>
          </Form.Row>

          <Form.Row>
            <Form.Group className="col-md-8">
              <Form.Label htmlFor="excel-profile-font">ฟอนต์ Excel</Form.Label>
              <Select
                inputId="excel-profile-font"
                isClearable={false}
                options={fontOptions}
                value={fontOptions.find(({ value }) => value === currentExportOptions.fontFamily) || fontOptions[0]}
                onChange={(option) => updateExportOption('fontFamily', option?.value || '')}
                menuPlacement="auto"
              />
            </Form.Group>
            <Form.Group className="col-md-4">
              <Form.Label htmlFor="excel-profile-font-size">ขนาดตัวอักษร (pt)</Form.Label>
              <Form.Control
                id="excel-profile-font-size"
                type="number"
                min={1}
                max={409}
                step="any"
                placeholder="อัตโนมัติ (12 pt)"
                value={currentExportOptions.fontSize}
                isInvalid={invalidFontSize}
                onChange={(event) => updateExportOption('fontSize', event.target.value)}
              />
              <Form.Control.Feedback type="invalid">ระบุขนาด 1–409 pt หรือเว้นว่างเพื่อใช้ 12 pt</Form.Control.Feedback>
            </Form.Group>
          </Form.Row>
          <div className="small text-muted mb-3">
            ใช้กับหัวตารางและข้อมูล Excel เมื่อบันทึก Profile แล้วให้บันทึกรายงานเพื่อใช้ครั้งถัดไป
          </div>
          <div className="table-responsive border">
            <table className="table table-sm table-bordered table-striped mb-0">
              <thead className="thead-light">
                <tr>
                  <th className="text-center" style={{ width: 70 }}>แสดง</th>
                  <th>Field</th>
                  <th>ชื่อคอลัมน์</th>
                  <th style={{ width: 190 }}>ชนิดข้อมูล</th>
                  <th style={{ width: 110 }}>ความกว้าง</th>
                  <th className="text-center" style={{ width: 110 }}>ลำดับ</th>
                </tr>
              </thead>
              <tbody>
                {columns.map((column, index) => (
                  <tr key={column.field}>
                    <td className="text-center align-middle">
                      <Form.Check
                        checked={column.visible}
                        disabled={!column.exportable}
                        onChange={(event) =>
                          updateColumn(column.field, { visible: event.target.checked })
                        }
                      />
                    </td>
                    <td className="align-middle">
                      <code>{column.field}</code>
                      {!column.exportable && (
                        <div className="small text-danger">ไม่อนุญาตให้ export</div>
                      )}
                    </td>
                    <td>
                      <Form.Control
                        disabled={!column.exportable}
                        onChange={(event) =>
                          updateColumn(column.field, { label: event.target.value })
                        }
                        value={column.label}
                      />
                    </td>
                    <td>
                      <Select
                        isClearable={false}
                        isDisabled={!column.exportable}
                        onChange={(option) =>
                          updateColumn(column.field, { type: option?.value || 'text' })
                        }
                        options={TYPE_OPTIONS}
                        value={
                          TYPE_OPTIONS.find(({ value }) => value === column.type) ||
                          TYPE_OPTIONS[0]
                        }
                      />
                    </td>
                    <td>
                      <Form.Control
                        disabled={!column.exportable}
                        min={6}
                        onChange={(event) =>
                          updateColumn(column.field, { width: event.target.value })
                        }
                        type="number"
                        value={column.width}
                      />
                    </td>
                    <td className="text-center align-middle">
                      <Button
                        className="mr-1 px-2"
                        title="เลื่อนคอลัมน์ขึ้น"
                        disabled={index === 0}
                        onClick={() => moveColumn(index, -1)}
                        size="sm"
                        variant="outline-secondary"
                      >
                        ↑
                      </Button>
                      <Button
                        className="px-2"
                        title="เลื่อนคอลัมน์ลง"
                        disabled={index === columns.length - 1}
                        onClick={() => moveColumn(index, 1)}
                        size="sm"
                        variant="outline-secondary"
                      >
                        ↓
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="small text-muted mt-2">
            ระบบตรวจชื่อและชนิดข้อมูลจาก datasource อัตโนมัติ โดย JSON Profile เป็น optional
            และจะบันทึกเฉพาะค่าที่ต่างจากค่าอัตโนมัติ
          </div>
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <div>
            <Button aria-label="คืนค่าอัตโนมัติ" title="คืนการตั้งค่า Excel เป็นค่าอัตโนมัติ" className="mr-2" onClick={resetProfile} variant="outline-secondary">
              คืนค่าอัตโนมัติ
            </Button>
            <Button aria-label="คัดลอก Profile JSON" title="คัดลอกการตั้งค่า Excel รวมฟอนต์" disabled={invalidFontSize} onClick={copyProfileJson} variant="outline-primary">
              คัดลอก Profile JSON
            </Button>
            {copyStatus && <span className="ml-2 small text-muted">{copyStatus}</span>}
          </div>
          <Button aria-label="บันทึก Profile" title="บันทึกคอลัมน์และฟอนต์ลงใน Profile ของรายงาน" disabled={invalidFontSize} onClick={applyProfile} variant="primary">
            บันทึก Profile
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default ExcelDataPreview
